// marked's GFM table rule requires every row on a single line. The legacy
// Showdown table extension supported cells that spanned multiple lines
// (including blank lines for paragraph breaks). This pre-processor restores
// that capability by joining continuation lines into the leading row line with
// a sentinel marker for each consumed newline. The marked table-cell renderer
// later re-parses cells containing the sentinel as block markdown so
// paragraphs, lists, and other block constructs render inside `<td>` / `<th>`
// instead of as inline `<br>`-separated text.

import { CELL_NL_SENTINEL } from './cellSentinel';

const ALIGNMENT_CELL_RE = /^:?-{3,}:?$/;

const countUnescapedPipes = (line: string): number => {
  let count = 0;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === '|' && (i === 0 || line[i - 1] !== '\\')) count++;
  }
  return count;
};

const startsWithPipe = (line: string): boolean =>
  line.trimStart().startsWith('|');

const isAlignmentRow = (line: string): boolean => {
  const cells = line
    .split('|')
    .map(cell => cell.trim())
    .filter(cell => cell.length > 0);
  return cells.length > 0 && cells.every(cell => ALIGNMENT_CELL_RE.test(cell));
};

const findAlignmentRow = (lines: string[], from: number): number => {
  for (let i = from; i < lines.length; i++) {
    if (isAlignmentRow(lines[i])) return i;
    if (i > from && startsWithPipe(lines[i])) {
      return -1;
    }
  }
  return -1;
};

const collapseMultilineTableRows = (markdown: string): string => {
  const lines = markdown.split('\n');
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    if (!startsWithPipe(lines[i])) {
      out.push(lines[i]);
      i++;
      continue;
    }

    const alignmentIdx = findAlignmentRow(lines, i);
    if (alignmentIdx === -1) {
      while (i < lines.length && startsWithPipe(lines[i])) {
        out.push(lines[i]);
        i++;
      }
      continue;
    }

    const expectedPipes = countUnescapedPipes(lines[alignmentIdx]);

    while (i < lines.length && startsWithPipe(lines[i])) {
      if (i === alignmentIdx) {
        out.push(lines[i]);
        i++;
        continue;
      }
      const rowParts = [lines[i]];
      let pipeCount = countUnescapedPipes(lines[i]);
      i++;
      while (
        pipeCount < expectedPipes &&
        i < lines.length &&
        !startsWithPipe(lines[i])
      ) {
        rowParts.push(lines[i]);
        pipeCount += countUnescapedPipes(lines[i]);
        i++;
      }
      out.push(rowParts.join(CELL_NL_SENTINEL));
    }
  }

  return out.join('\n');
};

export { collapseMultilineTableRows };
